import type { SupabaseClient } from "@supabase/supabase-js";
import type { WebinarRepository } from "./repository";
import {
  AdminActivity, AdminError, EmailDelivery, EmailQuota, Registration, UsageItem, RegistrationError, RegistrationErrorCode, RegistrationResult, SendProgress, Webinar,
  WebinarInput,
} from "./types";
import { SUPABASE_PUBLISHABLE_KEY, SUPABASE_URL } from "./config";
import { isUuid } from "./shared/validation";
import { validateWebinar } from "./webinarValidation";

// ══════════════════════════════════════════════════════════════════════════════
//   PRODUCTION BACKEND — Supabase
//   The browser only ever holds the publishable (anon) key. What each visitor can do is decided
//   by the database's Row Level Security, not by this file: visitors read published webinars
//   and seat totals; registering goes through the `register` Edge Function; everything else,
//   including adding and editing webinars, needs a signed-in administrator.
//   See supabase/migrations/ and docs/WEBINARS-PRODUCTION.md.
// ══════════════════════════════════════════════════════════════════════════════

type Row = Record<string, any>;

let clientPromise: Promise<SupabaseClient> | null = null;

/** Loaded on first use, so the prototype build never downloads the Supabase library */
function client(): Promise<SupabaseClient> {
  if (!SUPABASE_URL || !SUPABASE_PUBLISHABLE_KEY) {
    return Promise.reject(new Error(
      "Supabase isn't configured: set REACT_APP_SUPABASE_URL and REACT_APP_SUPABASE_PUBLISHABLE_KEY, then rebuild."));
  }
  clientPromise ??= import("@supabase/supabase-js").then(({ createClient }) =>
    createClient(SUPABASE_URL, SUPABASE_PUBLISHABLE_KEY, { auth: { persistSession: true, autoRefreshToken: true } }));
  return clientPromise;
}

/** Throws the Supabase error, if there is one */
function check(error: { message: string } | null) {
  if (error) throw new Error(error.message);
}

const PUBLIC_WEBINAR_COLUMNS =
  "id, slug, title, summary, description, topics, presenter_name, presenter_role, presenter_photo, start_at, end_at, " +
  "registration_deadline, capacity, status, meeting_platform, recording_url, resource_url";

const toWebinar = (row: Row): Webinar => ({
  id: row.id,
  slug: row.slug,
  title: row.title,
  summary: row.summary ?? "",
  description: row.description ?? "",
  topics: row.topics ?? [],
  presenter: row.presenter_name ?? undefined,
  presenterRole: row.presenter_role ?? undefined,
  presenterPhoto: row.presenter_photo ?? undefined,
  startAt: row.start_at,
  endAt: row.end_at,
  registrationDeadline: row.registration_deadline ?? undefined,
  capacity: row.capacity ?? undefined,
  status: row.status,
  meetingPlatform: row.meeting_platform ?? undefined,
  recordingUrl: row.recording_url ?? undefined,
  resourceUrl: row.resource_url ?? undefined,
  isDemo: false,
});

/** Empty optional fields are stored as null, so clearing one in the form clears it in the database */
const toWebinarRow = (w: WebinarInput): Row => ({
  slug: w.slug,
  title: w.title,
  summary: w.summary,
  description: w.description,
  topics: w.topics,
  presenter_name: w.presenter ?? null,
  presenter_role: w.presenterRole ?? null,
  presenter_photo: w.presenterPhoto ?? null,
  start_at: w.startAt,
  end_at: w.endAt,
  registration_deadline: w.registrationDeadline ?? null,
  capacity: w.capacity ?? null,
  status: w.status,
  meeting_platform: w.meetingPlatform ?? null,
  recording_url: w.recordingUrl ?? null,
  resource_url: w.resourceUrl ?? null,
});

const toUsageItem = (row: Row): UsageItem => ({
  metric: row.metric,
  label: row.label,
  used: row.used === null || row.used === undefined ? null : Number(row.used),
  limit: Number(row.limit_value),
  unit: row.unit,
  source: row.source,
  recordedAt: row.recorded_at ?? undefined,
  previous: row.previous_value === null || row.previous_value === undefined
    ? undefined
    : { value: Number(row.previous_value), recordedAt: row.previous_at },
});

/** From the database (snake_case) or the send-emails response (camelCase) */
const toQuota = (row: Row): EmailQuota => ({
  dailyLimit: row.daily_limit ?? row.dailyLimit ?? null,
  used: row.used ?? 0,
  dayRemaining: row.day_remaining ?? row.dayRemaining ?? null,
  resetsAt: row.resets_at ?? row.resetsAt,
  exhausted: Boolean(row.exhausted),
  monthlyLimit: row.monthly_limit ?? row.monthlyLimit ?? null,
  monthUsed: row.month_used ?? row.monthUsed ?? 0,
  monthRemaining: row.month_remaining ?? row.monthRemaining ?? null,
  monthResetsAt: row.month_resets_at ?? row.monthResetsAt,
  monthExhausted: Boolean(row.month_exhausted ?? row.monthExhausted),
  inFlight: row.in_flight ?? row.inFlight ?? 0,
  remaining: row.remaining ?? null,
  waiting: row.waiting ?? 0,
});

const toRegistration = (row: Row): Registration => ({
  id: row.id,
  reference: row.reference,
  webinarId: row.webinar_id,
  firstName: row.first_name,
  lastName: row.last_name,
  email: row.email,
  phone: row.phone,
  organisation: row.organisation ?? undefined,
  professionalRole: row.professional_role ?? undefined,
  status: row.status,
  privacyNoticeVersion: row.privacy_notice_version,
  marketingConsent: row.marketing_consent,
  instructionsSentAt: row.instructions_sent_manually_at ?? undefined,
  archivedAt: row.archived_at ?? undefined,
  roundStartAt: row.round_start_at ?? undefined,
  registeredAt: row.registered_at,
  updatedAt: row.updated_at,
});

const toDelivery = (row: Row): EmailDelivery => ({
  id: row.id,
  registrationId: row.registration_id,
  emailType: row.email_type,
  status: row.status,
  attemptCount: row.attempt_count,
  sendRound: row.send_round,
  lastError: row.last_error ?? undefined,
  sentAt: row.sent_at ?? undefined,
  deliveredAt: row.delivered_at ?? undefined,
  complainedAt: row.complained_at ?? undefined,
  updatedAt: row.updated_at,
});

/** Supabase returns at most 1,000 rows per request; this pages through the rest */
async function fetchAll(page: (from: number, to: number) => PromiseLike<{ data: Row[] | null; error: { message: string } | null }>) {
  const size = 1000;
  const rows: Row[] = [];
  for (let from = 0; ; from += size) {
    const { data, error } = await page(from, from + size - 1);
    check(error);
    rows.push(...(data ?? []));
    if (!data || data.length < size) return rows;
  }
}

/** The JSON body of an Edge Function's error response, or null for network failures */
async function functionErrorBody(error: unknown): Promise<Row | null> {
  const context = (error as { context?: unknown })?.context;
  if (context instanceof Response) {
    try {
      return await context.clone().json();
    } catch {
      return null;
    }
  }
  return null;
}

const toActivity = (row: Row): AdminActivity => ({
  id: Number(row.id),
  occurredAt: row.occurred_at,
  actorEmail: row.actor_email ?? null,
  action: row.action,
  outcome: row.outcome,
  webinarId: row.webinar_id ?? null,
  details: row.details ?? {},
});

const REGISTRATION_CODES: RegistrationErrorCode[] = ["duplicate", "full", "closed", "not_found", "invalid", "rate_limited"];

export function createSupabaseRepository(): WebinarRepository {
  const listeners = new Set<() => void>();
  const notifyChange = () => listeners.forEach(listener => listener());
  let authWatched = false;

  /** Signed-in admin calls: maps "not allowed" answers to AdminError */
  async function adminClient() {
    const supabase = await client();
    const { data } = await supabase.auth.getSession();
    if (!data.session) throw new AdminError("not_signed_in", "Your session has ended. Please sign in again.");
    return supabase;
  }

  /** Calls send-emails until the queue is empty (or stops moving), reporting after each batch */
  async function runSendEmails(first: Row, onProgress?: (progress: SendProgress) => void): Promise<SendProgress> {
    const supabase = await adminClient();
    const call = async (body: Row) => {
      const { data, error } = await supabase.functions.invoke("send-emails", { body });
      if (error) {
        const detail = await functionErrorBody(error);
        const code = detail?.code;
        if (code === "no_meeting_link") throw new AdminError("no_meeting_link", detail!.message);
        if (code === "webinar_cancelled") throw new AdminError("webinar_cancelled", detail!.message);
        if (code === "forbidden") throw new AdminError("not_admin", detail!.message);
        if (code === "unauthorised") throw new AdminError("not_signed_in", detail!.message);
        if (code === "rate_limited") throw new AdminError("rate_limited", detail!.message, Number(detail!.retryAfter) || undefined);
        throw new AdminError("unavailable", detail?.message ?? "Couldn't reach the email service. Check your connection and try again.");
      }
      return data as SendProgress & { requeued?: number };
    };

    let result = await call(first);
    const total: SendProgress = {
      queued: result.queued, sent: result.sent, failed: result.failed, remaining: result.remaining,
      emailEnabled: result.emailEnabled, quota: result.quota && toQuota(result.quota),
    };
    onProgress?.({ ...total });

    // Keep going while emails are left and each batch makes progress. A batch that sends
    // nothing means the rest are locked by another sender, waiting to retry, or held back
    // by the daily limit.
    for (let round = 0; total.emailEnabled && total.remaining > 0 && result.sent + result.failed > 0 && round < 500; round++) {
      result = await call({ action: "continue", webinarId: first.webinarId ?? undefined, emailType: first.action === "send_joining" ? "joining_instructions" : undefined });
      total.sent += result.sent;
      total.failed += result.failed;
      total.remaining = result.remaining;
      total.quota = result.quota && toQuota(result.quota);
      onProgress?.({ ...total });
    }
    notifyChange();
    return total;
  }

  return {
    backend: "supabase",

    // ── Public ──

    async listPublicWebinars() {
      const supabase = await client();
      const { data, error } = await supabase.from("webinar_events").select(PUBLIC_WEBINAR_COLUMNS)
        .neq("status", "draft").order("start_at");
      check(error);
      return ((data ?? []) as unknown as Row[]).map(toWebinar);
    },

    async getPublicWebinar(slug) {
      const supabase = await client();
      const { data, error } = await supabase.from("webinar_events").select(PUBLIC_WEBINAR_COLUMNS)
        .eq("slug", slug).neq("status", "draft").maybeSingle();
      check(error);
      return data ? toWebinar(data as unknown as Row) : undefined;
    },

    async countActiveRegistrations() {
      const supabase = await client();
      const { data, error } = await supabase.rpc("webinar_seat_counts");
      check(error);
      return Object.fromEntries(((data ?? []) as Row[]).map(row => [row.webinar_id, row.active_registrations]));
    },

    async register(data, meta): Promise<RegistrationResult> {
      let supabase: SupabaseClient;
      try {
        supabase = await client();
      } catch {
        throw new RegistrationError("unavailable", "Registration is temporarily unavailable. Please try again later.");
      }
      const { data: result, error } = await supabase.functions.invoke("register", { body: { ...data, ...meta } });
      if (error) {
        const detail = await functionErrorBody(error);
        const code = REGISTRATION_CODES.find(c => c === detail?.code);
        if (code) throw new RegistrationError(code, detail!.message, detail!.errors, Number(detail!.retryAfter) || undefined);
        if (detail?.code === "spam") throw new RegistrationError("invalid", detail.message);
        throw new RegistrationError("unavailable", "We couldn't save your registration. Check your connection and try again.");
      }
      return {
        reference: result.reference,
        registeredAt: result.registeredAt,
        confirmationEmail: result.emailStatus,
      };
    },

    // ── Admin ──

    async getAdminSession() {
      const supabase = await client();
      if (!authWatched) {
        authWatched = true;
        supabase.auth.onAuthStateChange(event => {
          if (event === "SIGNED_IN" || event === "SIGNED_OUT") notifyChange();
        });
      }
      const { data } = await supabase.auth.getSession();
      if (!data.session) return null;
      // admin or none
      const { data: access, error } = await supabase.rpc("admin_access");
      check(error);
      return { email: data.session.user.email ?? "", isAdmin: access === "admin" };
    },

    async signIn(email, password) {
      const supabase = await client();
      const { error } = await supabase.auth.signInWithPassword({ email: email.trim(), password });
      if (error) {
        // Supabase Auth limits sign-in attempts per IP address
        if (error.status === 429) throw new AdminError("rate_limited", "Too many attempts. Please wait before trying again.");
        // The same answer whether or not the account exists
        throw new AdminError("sign_in_failed", "That email and password didn't match an account.");
      }
      // Noted in the activity log (administrators only); if that fails, the sign-in still goes ahead
      await supabase.rpc("record_admin_sign_in").then(() => undefined, () => undefined);
      notifyChange();
    },


    async signOut() {
      const supabase = await client();
      await supabase.auth.signOut();
      notifyChange();
    },

    async listAllWebinars() {
      const supabase = await adminClient();
      const { data, error } = await supabase.from("webinar_events").select(PUBLIC_WEBINAR_COLUMNS).order("start_at");
      check(error);
      return ((data ?? []) as unknown as Row[]).map(toWebinar);
    },

    async saveWebinar(input, id) {
      if (Object.keys(validateWebinar(input)).length > 0) throw new AdminError("invalid", "Check the highlighted fields.");
      const supabase = await adminClient();
      // The database assigns new IDs, and only administrators may insert or update (Row Level Security).
      // Reopening an archived webinar archives its registrations in the same transaction (a database trigger).
      const row = toWebinarRow(input);
      const { data, error } = id
        ? await supabase.from("webinar_events").update(row).eq("id", id).select(PUBLIC_WEBINAR_COLUMNS)
        : await supabase.from("webinar_events").insert(row).select(PUBLIC_WEBINAR_COLUMNS);
      if (error) {
        const code = (error as { code?: string }).code;
        if (code === "23505") throw new AdminError("duplicate", "Another webinar already uses this web address.");
        if (code === "23514") throw new AdminError("invalid", "The database refused these details. Check the dates and web addresses.");
        if (code === "42501") throw new AdminError("not_admin", "This account can't add or edit webinars.");
        throw new Error(error.message);
      }
      const saved = (data ?? []) as unknown as Row[];
      if (!saved.length) throw new AdminError("not_admin", "This account can't edit this webinar.");
      notifyChange();
      return toWebinar(saved[0]);
    },

    async listRegistrations(filters = {}) {
      const supabase = await adminClient();
      const rows = await fetchAll((from, to) => {
        let query = supabase.from("webinar_registrations").select("*")
          .order("registered_at", { ascending: filters.sort === "oldest" }).range(from, to);
        if (filters.webinarId) query = query.eq("webinar_id", filters.webinarId);
        if (filters.status) query = query.eq("status", filters.status);
        const round = filters.round ?? "current";
        if (round === "current") query = query.is("archived_at", null);
        if (round === "archived") query = query.not("archived_at", "is", null);
        return query;
      });
      // Searched here rather than in SQL, so "first name + surname" matches too
      const search = filters.search?.trim().toLowerCase();
      return rows.map(toRegistration).filter(r => !search ||
        `${r.firstName} ${r.lastName}`.toLowerCase().includes(search) ||
        r.email.includes(search) ||
        r.reference.toLowerCase().includes(search));
    },

    async updateRegistration(id, data) {
      const supabase = await adminClient();
      const patch: Row = {};
      if (data.status) patch.status = data.status;
      if (data.instructionsSentAt !== undefined) patch.instructions_sent_manually_at = data.instructionsSentAt;
      const { data: updated, error } = await supabase.from("webinar_registrations").update(patch).eq("id", id).select("id");
      if (error) {
        if ((error as { code?: string }).code === "23505") {
          throw new AdminError("duplicate", "This person already has another active registration for this webinar.");
        }
        throw new Error(error.message);
      }
      if (!updated?.length) throw new AdminError("not_admin", "This account can't change registrations.");
      notifyChange();
    },

    async listEmailDeliveries() {
      const supabase = await adminClient();
      const rows = await fetchAll((from, to) =>
        supabase.from("email_delivery_log").select("*").order("created_at").range(from, to));
      return rows.map(toDelivery);
    },

    async getMeetingLinks() {
      const supabase = await adminClient();
      const { data, error } = await supabase.from("webinar_meeting_links").select("webinar_id, meeting_link");
      check(error);
      return Object.fromEntries(((data ?? []) as Row[]).map(row => [row.webinar_id, row.meeting_link]));
    },

    async saveMeetingLink(webinarId, link) {
      const supabase = await adminClient();
      const trimmed = link.trim();
      const { error } = trimmed
        ? await supabase.from("webinar_meeting_links").upsert({ webinar_id: webinarId, meeting_link: trimmed }, { onConflict: "webinar_id" })
        : await supabase.from("webinar_meeting_links").delete().eq("webinar_id", webinarId);
      if (error) {
        if ((error as { code?: string }).code === "23514") throw new AdminError("invalid", "Paste the full joining link, starting with https://");
        throw new Error(error.message);
      }
      notifyChange();
    },

    async sendJoiningInstructions(request, onProgress) {
      if (request.registrationIds && !request.registrationIds.every(isUuid)) throw new AdminError("invalid", "Choose who to send to.");
      return runSendEmails({ action: "send_joining", ...request }, onProgress);
    },

    async retryFailedEmails(webinarId, onProgress) {
      return runSendEmails({ action: "retry_failed", webinarId }, onProgress);
    },

    async sendWaitingEmails(webinarId, onProgress) {
      return runSendEmails({ action: "continue", webinarId: webinarId ?? undefined }, onProgress);
    },

    async getProjectUsage() {
      const supabase = await adminClient();
      const { data, error } = await supabase.rpc("project_usage_status");
      check(error);
      return ((data ?? []) as Row[]).map(toUsageItem);
    },

    async recordProjectUsage(metric, value) {
      const supabase = await adminClient();
      const { error } = await supabase.rpc("record_project_usage", { p_metric: metric, p_value: value });
      if (error) {
        if (/KM_INVALID/.test(error.message)) throw new AdminError("invalid", "Enter a figure of zero or more.");
        if (/KM_NOT_ADMIN/.test(error.message)) throw new AdminError("not_admin", "This account can't record usage.");
        throw new Error(error.message);
      }
      notifyChange();
    },

    async listAdminActivity(limit) {
      const supabase = await adminClient();
      const { data, error } = await supabase.from("admin_audit_log")
        .select("id, occurred_at, actor_email, action, outcome, webinar_id, details")
        .order("occurred_at", { ascending: false })
        .limit(limit);
      check(error);
      return ((data ?? []) as Row[]).map(toActivity);
    },

    async getEmailQuota() {
      const supabase = await adminClient();
      const { data, error } = await supabase.rpc("email_quota_status");
      check(error);
      return toQuota(((data ?? []) as Row[])[0] ?? {});
    },

    subscribe(listener) {
      listeners.add(listener);
      return () => { listeners.delete(listener); };
    },
  };
}
